/**
 * Auth provider that completes the login redirect through React Router.
 *
 * What: Wraps `DevAuthProvider` and wires Auth0's `onRedirectCallback` to
 * `useNavigate`, so `loginWithRedirect({ appState: { returnTo } })` is the one
 * post-login destination mechanism app-wide.
 * Why: The SDK's default callback rewrites the URL with raw
 * `history.replaceState`, which React Router never observes — `appState.returnTo`
 * was silently dropped and users were bounced to the landing page.
 * When: Update when changing the post-login navigation contract or `/callback`
 * handling (see `AuthCallback.tsx` for the no-returnTo role-default path).
 *
 * Must render inside `BrowserRouter` (it calls `useNavigate`).
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AppState } from '@auth0/auth0-react';

import { DevAuthProvider, type DevAuthProviderProps } from './DevAuthProvider';

type Auth0ProviderWithNavigateProps = Omit<
  DevAuthProviderProps,
  'onRedirectCallback'
>;

export const Auth0ProviderWithNavigate = ({
  children,
  ...auth0ProviderProps
}: Auth0ProviderWithNavigateProps) => {
  const navigate = useNavigate();

  const onRedirectCallback = useCallback(
    (appState?: AppState) => {
      // With a returnTo, go straight there (protected routes handle the rest).
      // Without one, stay on /callback minus the code/state params so
      // AuthCallback can resolve the role-default destination once the role
      // loads — the role isn't known yet at exchange time.
      navigate(appState?.returnTo || window.location.pathname, {
        replace: true,
      });
    },
    [navigate]
  );

  return (
    <DevAuthProvider
      {...auth0ProviderProps}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </DevAuthProvider>
  );
};
